import { useIsCountrySpecificEhrSelectionEnabled } from '@/features/flags';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useQueryUser, useUser } from '@/hooks/query/useQueryUser';
import { LanguageCode } from '@heidi/schema/ml-scribe';
import { type CountryType } from '@heidi/utils/countries';
import { getAvailableDisplayLanguagesForCountry } from '@heidi/utils/i18n';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useOnboardingAnalytics } from '@/models/analytics/features/useOnboardingAnalytics';
import {
  useDisplayLanguagePersistence,
  useOnboardingNavigationHandlers,
} from '@heidi/growth';
import type { OnboardingFlowKind } from '@heidi/growth/contracts';
import { useDocumentSelection } from '../hooks/useDocumentSelection';
import { useOnboardingCompletion } from '../hooks/useOnboardingCompletion';
import { useOnboardingLocaleSync } from '../hooks/useOnboardingLocaleSync';
import { usePersonalizationForm } from '../hooks/usePersonalizationForm';
import { useTemplateGeneration } from '../hooks/useTemplateGeneration';
import { useWorkspaceNamePrefill } from '../hooks/useWorkspaceNamePrefill';
import {
  OnboardingContext,
  type OnboardingCompletionState,
  type OnboardingContextValue,
} from './OnboardingContext';
import { useMicTestState } from './useMicTestState';
import { useDocumentState, useTemplateState } from './useOnboardingDerivedState';
import {
  buildSeedFromSavedUser,
  useSeedFormFromSavedUser,
} from './useSeedFormFromSavedUser';

interface OnboardingProviderProps {
  children: React.ReactNode;
  onComplete: () => void;
  flowKind?: OnboardingFlowKind;
  resumeAfterAuthOnboard?: boolean;
}

export function OnboardingProvider({
  children,
  onComplete,
  flowKind = 'v2',
  resumeAfterAuthOnboard = false,
}: OnboardingProviderProps) {
  const { data: savedUser } = useQueryUser();
  const isCountryScopedEhrList = useIsCountrySpecificEhrSelectionEnabled();
  const personalization = usePersonalizationForm({
    flowKind,
    resumeAfterAuthOnboard,
    savedUserSeed:
      resumeAfterAuthOnboard && savedUser ? buildSeedFromSavedUser(savedUser) : undefined,
  });
  const {
    formData,
    updateFormData,
    currentStep,
    currentStepIndex,
    steps,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    isLastStep,
    isGuidedTour,
    isGuidedTourEligible,
  } = personalization;

  const analytics = useOnboardingAnalytics({ isGuidedTour, isGuidedTourEligible });

  const templateHook = useTemplateGeneration({
    formData,
    updateFormData,
    goToStep,
    steps,
    analytics,
  });

  const docHook = useDocumentSelection({
    specialtyId: formData.specialty?.id,
    language: formData.displayLanguage ?? undefined,
    country: formData.country?.name ?? undefined,
  });

  const { data: kindeProperties } = useQueryKindeProperties();
  const { data: userEmail } = useUser((data) => data?.email);
  const hasTrackedStartRef = useRef(false);
  const { persistLanguage } = useDisplayLanguagePersistence();
  useOnboardingLocaleSync(formData.displayLanguage, {
    onLanguageChanged: persistLanguage,
  });

  useEffect(() => {
    if (!kindeProperties || hasTrackedStartRef.current) return;
    hasTrackedStartRef.current = true;
    analytics.trackOnboardingStarted({ region: kindeProperties.region });
  }, [analytics, kindeProperties]);

  useWorkspaceNamePrefill(
    currentStep,
    formData.firstName,
    formData.clinicName,
    updateFormData
  );

  const handleCountryChange = useCallback(
    (value: CountryType | null, options?: { isSeed?: boolean }) => {
      updateFormData('country', value);
      updateFormData('jurisdiction', '');
      // The specialty option list is scoped per country — a previously picked
      // specialty may not exist in the new country's list, so clear it.
      updateFormData('specialty', null);
      updateFormData('otherSpecialty', '');
      if (isCountryScopedEhrList && !options?.isSeed) {
        // The EHR option list is scoped per country too, so a previously
        // picked provider may not exist in the new country's list. A seed is
        // exempt: it re-establishes the country already in effect when the
        // EHR was picked, and nothing re-applies emrEhr afterwards.
        updateFormData('emrEhr', null);
      }
      const [primaryLang] = getAvailableDisplayLanguagesForCountry(value?.countryCode);
      updateFormData('displayLanguage', primaryLang ?? LanguageCode.en);
    },
    [updateFormData, isCountryScopedEhrList]
  );

  useSeedFormFromSavedUser(updateFormData, handleCountryChange, {
    resumeAfterAuthOnboard,
  });

  const {
    completeOnboarding,
    isSubmitting: isCompletingOnboarding,
    error: onboardingError,
  } = useOnboardingCompletion({
    formData,
    analytics,
    submitFavorites: docHook.submitFavorites,
    onSuccess: onComplete,
    resumeAfterAuthOnboard,
  });

  const generatedTemplateDisplayName = formData.generatedTemplateName ?? '';
  const generatedTemplateDisplayContent = formData.generatedTemplateStructure ?? '';

  const micTestState = useMicTestState();
  const {
    setPhase: setMicPhase,
    setTranscript: setMicTranscript,
    setRecordingDurationSec: setMicRecordingDuration,
    setEditActivity: setMicEditActivity,
  } = micTestState;

  const { handleBack, registerNoteEditBackHandler } = useOnboardingNavigationHandlers({
    currentStep,
    generatedTemplateDisplayName,
    goToStep,
    goToPreviousStep,
    setMicPhase,
    setMicTranscript,
    setMicRecordingDuration,
    setMicEditActivity,
  });

  const handleContinue = useCallback(() => {
    analytics.trackStepCompleted(currentStep, currentStepIndex + 1);
    if (isLastStep) onComplete();
    else goToNextStep();
  }, [analytics, currentStep, currentStepIndex, goToNextStep, isLastStep, onComplete]);

  const submitAndAdvance = useCallback(
    (trackAsSkip: boolean) => {
      if (!trackAsSkip) {
        analytics.trackDocumentsSelected({
          count: docHook.selectedIds.size,
          docTypes: docHook.selectedTemplates.map((t) => t.name),
        });
        analytics.trackStepCompleted('document-selection', currentStepIndex + 1);
      } else {
        analytics.trackStepSkipped('document-selection');
      }
      goToNextStep();
    },
    [
      analytics,
      currentStepIndex,
      goToNextStep,
      docHook.selectedIds.size,
      docHook.selectedTemplates,
    ]
  );

  const onMicSkip = useCallback(() => {
    analytics.trackStepSkipped('mic-permissions');
    if (isLastStep) onComplete();
    else goToNextStep();
  }, [analytics, goToNextStep, isLastStep, onComplete]);

  useEffect(() => {
    if (currentStep === 'document-selection' && !docHook.isLoading && docHook.hasError)
      goToNextStep();
  }, [currentStep, docHook.isLoading, docHook.hasError, goToNextStep]);

  const templateState = useTemplateState(
    templateHook,
    generatedTemplateDisplayName,
    generatedTemplateDisplayContent,
    formData.generatedTemplateName
  );

  const documentState = useDocumentState(docHook);

  const onGetStarted = useCallback(() => {
    void (async () => {
      analytics.trackStepCompleted('completion', currentStepIndex + 1);
      await completeOnboarding();
    })();
  }, [analytics, completeOnboarding, currentStepIndex]);

  const completionState = useMemo<OnboardingCompletionState>(
    () => ({
      completeOnboarding,
      onGetStarted,
      isCompletingOnboarding,
      onboardingError,
    }),
    [completeOnboarding, isCompletingOnboarding, onboardingError, onGetStarted]
  );

  const value = useMemo<OnboardingContextValue>(
    () => ({
      ...personalization,
      analytics,
      template: templateState,
      documents: documentState,
      completion: completionState,
      micTest: micTestState,
      userEmail: userEmail ?? undefined,
      handleCountryChange,
      handleBack,
      handleContinue,
      submitAndAdvance,
      onMicSkip,
      registerNoteEditBackHandler,
    }),
    [
      personalization,
      analytics,
      templateState,
      documentState,
      completionState,
      micTestState,
      userEmail,
      handleCountryChange,
      handleBack,
      handleContinue,
      submitAndAdvance,
      onMicSkip,
      registerNoteEditBackHandler,
    ]
  );

  return (
    <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
  );
}
